import { makeAutoObservable, reaction, type IReactionDisposer } from 'mobx'
import { nanoid } from 'nanoid'
import { showToast } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { TableStore } from 'shared/ui/TableNew'
import { DeveloperAuthApi } from 'entities/Auth/api'
import { type IResponseAuthToken } from 'entities/Auth'
import { AuthorizedAppRow } from 'pages/settings/pages/developer/pages/authorizedApps/model/AuthorizedAppRow'

export class AuthorizedAppsStore {
  private _loading = false
  private _tokens: IResponseAuthToken[] = []
  private readonly _tableStore: TableStore<AuthorizedAppRow>
  private readonly _listMap = new Map<string, AuthorizedAppRow>()
  private readonly _revokeModalId = nanoid()
  private _revokingToken: IResponseAuthToken | null = null
  private _revoking = false
  private _revokeModalDisposeReaction: IReactionDisposer | null = null

  constructor() {
    makeAutoObservable(this)

    this._tableStore = new TableStore<AuthorizedAppRow>({
      key: 'developer_authorized_apps',
    })
  }

  init = async () => {
    await this.loadTokens()
  }

  loadTokens = async () => {
    this._loading = true

    try {
      const { data } = await DeveloperAuthApi.getAuthTokens()
      const list = data ?? []

      this._tokens = list
      this._listMap.clear()

      const rows = list.map((item) => new AuthorizedAppRow(item))

      rows.forEach((row) => this._listMap.set(String(row.id), row))

      this._tableStore.setRows(rows)
      this._tableStore.setTotal(rows.length)
    } finally {
      this._loading = false
    }
  }

  openRevokeModal = (token: IResponseAuthToken) => {
    this._revokingToken = token
    this._revoking = false

    modalStore.addModal({
      id: this._revokeModalId,
      type: ModalTypeList.ALERT,
      width: 480,
      title: 'Revoke token?',
      desc: 'This action cannot be undone',
      showCloseButton: true,
      closeText: 'Cancel',
      primaryAction: {
        text: 'Yes, revoke',
        disabled: false,
        onAction: () => this.handleRevokeToken(),
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: () => this.closeRevokeModal(),
      },
      onClose: () => this.closeRevokeModal(),
    })

    this._revokeModalDisposeReaction?.()
    this._revokeModalDisposeReaction = reaction(
      () => this._revoking,
      (revoking) => {
        const modal = modalStore.getModal(this._revokeModalId)
        if (modal && 'primaryAction' in modal && modal.primaryAction) {
          modalStore.removeModal(this._revokeModalId)
          modalStore.addModal({
            ...modal,
            primaryAction: {
              ...modal.primaryAction,
              disabled: revoking,
            },
          })
        }
      }
    )
  }

  closeRevokeModal = () => {
    this._revokeModalDisposeReaction?.()
    this._revokeModalDisposeReaction = null
    this._revokingToken = null
    this._revoking = false

    modalStore.removeModal(this._revokeModalId)
  }

  handleRevokeToken = async () => {
    const token = this._revokingToken
    if (!token) return

    this._revoking = true

    try {
      await DeveloperAuthApi.revokeAuthToken(token.id)
      this.closeRevokeModal()

      showToast({ type: 'success', title: 'Token revoked' })

      this._tokens = this._tokens.filter((t) => t.id !== token.id)
      this._listMap.delete(token.id)

      const rows = Array.from(this._listMap.values())

      this._tableStore.setRows(rows)
      this._tableStore.setTotal(rows.length)
    } catch {
      showToast({ type: 'error', title: 'Failed to revoke access' })
    } finally {
      this._revoking = false
    }
  }

  dispose = () => {
    this._revokeModalDisposeReaction?.()
    this._revokeModalDisposeReaction = null

    modalStore.removeModal(this._revokeModalId)
  }

  get loading() {
    return this._loading
  }

  get tokens() {
    return this._tokens
  }

  get tableStore() {
    return this._tableStore
  }

  get items() {
    return Array.from(this._listMap.values())
  }
}
